import { useState, useEffect } from "react";
import { Check, Edit, Loader2, ArrowRight, ArrowLeft } from "lucide-react";
import Logo from "./Logo";
import StepIndicator from "./StepIndicator";

// Helper function to generate greeting
const generateGreeting = (businessName) => {
  return `Hello! Welcome to ${businessName}. How can I help you today?`;
};

// Helper function to generate FAQs - you'll need to import/copy this from OnboardingApp
const generateFAQsFromBusiness = (businessData) => {
  const faqs = [];
  
  // Contact FAQ
  if (businessData.phone || businessData.website) {
    faqs.push({
      question: `How can I contact ${businessData.businessName}?`,
      answer: `You can reach us${businessData.phone ? ` by phone at ${businessData.phone}` : ''}${businessData.phone && businessData.website ? ' or' : ''}${businessData.website ? ` visit our website at ${businessData.website}` : ''}.`
    });
  }
  
  // Location FAQ
  if (businessData.address) {
    faqs.push({
      question: `Where is ${businessData.businessName} located?`,
      answer: `We are located at ${businessData.address}.`
    });
  }
  
  // Hours FAQ
  if (businessData.openingHours && businessData.openingHours.weekdayText) {
    faqs.push({
      question: `What are your opening hours?`,
      answer: `Our opening hours are: ${businessData.openingHours.weekdayText.slice(0, 2).join(', ')}.`
    });
  }
  
  return faqs;
};

export default function StepTwoVerify({
  formData,
  handleSubmit,
  setCurrentStep,
  isLoading,
  handleInputChange,
  handleCancel,
}) {
  const [editingField, setEditingField] = useState(null);
  const [editedValue, setEditedValue] = useState("");

  // Enhanced logging and data validation
  useEffect(() => {
    // console.log('🔎 StepTwoVerify formData:', formData);
    // console.log('📋 extractedInfo:', formData?.extractedInfo);
    
    // Validate extractedInfo structure
    const extractedInfo = formData?.extractedInfo || {};
    // console.log('📊 Extracted Info Details:', {
    //   businessName: extractedInfo.businessName || 'undefined',
    //   industry: extractedInfo.industry || 'undefined',
    //   description: extractedInfo.description || 'undefined',
    //   services: Array.isArray(extractedInfo.services) ? extractedInfo.services : 'undefined',
    //   isValidStructure: !!(extractedInfo.businessName && extractedInfo.industry && extractedInfo.description && Array.isArray(extractedInfo.services))
    // });
  }, [formData]);

  const handleEditClick = (field, currentValue) => {
    setEditingField(field);
    setEditedValue(Array.isArray(currentValue) ? currentValue.join("\n") : currentValue);
  };

  const handleSaveEdit = (field) => {
  // Trim the edited value to check for empty or space-only content
  const trimmedValue = editedValue.trim();

  if (!trimmedValue) {
    // Prevent saving if the field is empty or just whitespace
    console.error("Field cannot be empty.");
    return;
  }

  let newValue = trimmedValue;
  if (field === "extractedInfo.services") {
    newValue = trimmedValue.split("\n").map(s => s.trim()).filter(s => s.length > 0);
  }
  
  // Special handling for business name - update both fields and regenerate dynamic content
  if (field === "extractedInfo.businessName") {
    handleInputChange("extractedInfo.businessName", newValue);
    handleInputChange("businessName", newValue);
    
    // Regenerate greeting with new business name
    const newGreeting = generateGreeting(newValue);
    handleInputChange("greeting", newGreeting);
    
    // Regenerate FAQs with new business name
    const businessData = {
      businessName: newValue,
      phone: formData.googleBusinessProfile?.phone || formData.phoneNumber,
      website: formData.googleBusinessProfile?.website || formData.websiteAddress,
      address: formData.googleBusinessProfile?.address || formData.extractedInfo?.address,
      openingHours: formData.googleBusinessProfile?.openingHours || formData.extractedInfo?.openingHours,
      businessTypes: formData.googleBusinessProfile?.businessTypes || [],
      rating: formData.googleBusinessProfile?.rating || formData.extractedInfo?.rating,
      totalReviews: formData.googleBusinessProfile?.totalReviews || formData.extractedInfo?.totalReviews
    };
    
    const newFAQs = generateFAQsFromBusiness(businessData);
    if (newFAQs.length > 0) {
      handleInputChange("faqs", newFAQs);
    }
    
    console.log('Updated business name and regenerated greeting/FAQs:', {
      newBusinessName: newValue,
      newGreeting,
      newFAQs
    });
  } else {
    handleInputChange(field, newValue);
  }
  
  setEditingField(null);
};

  const renderField = (label, field, value) => {
    const isEditing = editingField === field;
    const isList = Array.isArray(value);
    const inputType = field === "extractedInfo.description" || field === "extractedInfo.services" ? "textarea" : "input";

    return (
      <div className="p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">
            {label}:
          </span>
          <button
            onClick={() => handleEditClick(field, value)}
            className="text-blue-500 hover:text-blue-700 transition-colors"
            aria-label={`Edit ${label}`}
          >
            <Edit size={16} />
          </button>
        </div>
        {isEditing ? (
          <div className="flex items-start space-x-2">
            {inputType === "textarea" ? (
              <textarea
                value={editedValue}
                onChange={(e) => setEditedValue(e.target.value)}
                className="flex-1 px-2 py-1 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 min-h-[100px]"
              />
            ) : (
              <input
                type="text"
                value={editedValue}
                onChange={(e) => setEditedValue(e.target.value)}
                className="flex-1 px-2 py-1 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500"
              />
            )}
            <button
              onClick={() => handleSaveEdit(field)}
              // Disable the save button if the edited value is empty or just whitespace
              disabled={!editedValue.trim()}
              className="px-3 py-1 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save
            </button>
          </div>
        ) : isList ? (
          <ul className="list-disc list-inside space-y-1 text-sm text-gray-900">
            {value.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-900">{value}</p>
        )}
      </div>
    );
  };

  return (    
      <div className="min-h-screen flex items-start justify-center pt-1 p-4" style={{ backgroundColor: '#ffffff' }}>
      <div className="bg-white rounded-2xl   p-8 w-full max-w-md">
        <Logo />
        <StepIndicator currentStep={2} />

        <h2 className="text-xl font-semibold text-gray-900 mb-6">
          Verify Your Details
        </h2>

        {formData?.extractedInfo ? (
          <div className="space-y-4 mb-6">
           
            {renderField("Business Name", "extractedInfo.businessName", formData.extractedInfo.businessName || "Not found")}

            {renderField("Industry", "extractedInfo.industry", formData.extractedInfo.industry || "General Business")}
            {renderField("Description", "extractedInfo.description", formData.extractedInfo.description || "No description available.")}
            {renderField("Services", "extractedInfo.services", Array.isArray(formData.extractedInfo.services) ? formData.extractedInfo.services : [])}
            
            {/* Debug Information */}
            {/* <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h3 className="text-sm font-medium text-yellow-800 mb-2">Debug Information</h3>
              <pre className="text-xs text-yellow-700 whitespace-pre-wrap">
                {JSON.stringify({
                  hasExtractedInfo: !!formData.extractedInfo,
                  businessName: formData.extractedInfo.businessName,
                  industry: formData.extractedInfo.industry,
                  description: formData.extractedInfo.description,
                  services: formData.extractedInfo.services,
                  rawData: formData.extractedInfo
                }, null, 2)}
              </pre>
            </div> */}
          </div>
        ) : (
          <div className="p-4 bg-gray-50 rounded-lg mb-6">
            <p className="text-sm text-gray-700">Loading business information...</p>
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={isLoading}
          className="w-full py-4 bg-gradient-to-r from-green-500 to-teal-600 text-white rounded-lg hover:from-green-600 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center space-x-2"
        >
          {isLoading ? (
            <>
              <Loader2 size={20} className="animate-spin" />
              <span>Finishing Up...</span>
            </>
          ) : (
            <>
              <Check size={20} />
              <span>Next Step</span>
            </>
          )}
        </button>

        <div className="flex justify-between items-center mt-4">
          <button
            onClick={handleCancel}
            className="py-3 px-4 text-red-600 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
